import React, { useRef, useState } from "react";
import api from "../api";
import { Upload, Loader2, X } from "lucide-react";
import { toast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Reusable image upload: uploads to server, stores absolute URL. Also allows pasting a URL.
export default function ImageUpload({ value, onChange, label, compact, hint, square }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current && inputRef.current.click();

  const cropToSquare = (file) => new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const side = Math.min(img.width, img.height);
        const out = Math.min(side, 1200);
        const canvas = document.createElement("canvas");
        canvas.width = out; canvas.height = out;
        const ctx = canvas.getContext("2d");
        const sx = (img.width - side) / 2;
        const sy = (img.height - side) / 2;
        ctx.drawImage(img, sx, sy, side, side, 0, 0, out, out);
        URL.revokeObjectURL(url);
        canvas.toBlob((blob) => {
          if (!blob) return resolve(file);
          const name = (file.name || "image").replace(/\.[^.]+$/, "") + ".jpg";
          resolve(new File([blob], name, { type: "image/jpeg" }));
        }, "image/jpeg", 0.9);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    } catch { resolve(file); }
  });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const toUpload = square ? await cropToSquare(file) : file;
      const fd = new FormData();
      fd.append("file", toUpload, toUpload.name);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const abs = data.url.startsWith("http") ? data.url : `${BACKEND_URL}${data.url}`;
      onChange(abs);
      toast({ title: square ? "Image uploaded & cropped to square" : "Image uploaded" });
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail || "Try a smaller image", variant: "destructive" });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      {label && <label className="block text-sm font-medium text-[#16243b] mb-1.5">{label}</label>}
      <div className="flex items-center gap-2">
        {value ? (
          <div className="relative shrink-0">
            <img src={value} alt="" className={`rounded-lg object-cover border border-gray-200 ${compact ? "w-10 h-10" : "w-14 h-14"}`} />
            <button type="button" onClick={() => onChange("")} className="absolute -top-2 -right-2 bg-white rounded-full border border-gray-200 p-0.5 text-[#5a6b80] hover:text-red-500"><X size={13} /></button>
          </div>
        ) : null}
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL or upload"
          className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
        <button type="button" onClick={pick} disabled={busy}
          className="gp-btn-outline rounded-lg px-3 py-2 text-sm font-medium flex items-center gap-1 shrink-0 disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Upload
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
      {hint && <p className="text-xs text-[#8a97a8] mt-1.5">{hint}</p>}
    </div>
  );
}
